import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule, MatFormFieldAppearance } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Test } from '../../dto/test';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { Subject, takeUntil } from 'rxjs';
import { TestService } from '../../service/test.service';
import { Router } from '@angular/router';
import { BoolDto } from '../../../../common/dto/bool-dto';
import { ConfirmDialogComponent } from '../../../../common/component/dialog/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'dpi-test-details',
  imports: [FormsModule, MatFormFieldModule, MatInputModule, MatCardModule, MatIconModule, MatButtonModule, MatToolbarModule,
    MatTooltipModule, MatSelectModule, MatDialogModule, NgIf],
  templateUrl: './test-details.component.html',
  styleUrl: './test-details.component.scss'
})
export class TestDetailsComponent implements OnInit, OnDestroy {
  test: Test;
  isReadonly: boolean;
  fieldAppearance: MatFormFieldAppearance;
  destroyed$ = new Subject<void>(); // notifier of component being destroyed
  isContentChanged: boolean;
  private _dialogConfirm = inject(MatDialog);

  image_required_options: BoolDto[];
  crew_perform_options: BoolDto[];
  test_completed_options: BoolDto[];

  image_required: BoolDto;
  crew_perform: BoolDto;
  test_completed: BoolDto;

  constructor(private notifyService: StateNotifyService, private testService: TestService, private router: Router) {
    this.test = this.notifyService.getSelectedTest();
    this.isReadonly = true;
    this.fieldAppearance = 'outline';
    this.isContentChanged = false;

    this.image_required_options = this.testService.getAllTestBinaryStates();
    this.crew_perform_options = this.testService.getAllTestBinaryStates();
    this.test_completed_options = this.testService.getAllTestBinaryStates();

    this.image_required = this.testService.getTestBinaryState(this.image_required_options, this.test.images_required);
    this.crew_perform = this.testService.getTestBinaryState(this.crew_perform_options, this.test.crew_perform);
    this.test_completed = this.testService.getTestBinaryState(this.test_completed_options, this.test.test_completed);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.testSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.test = model;
        this.image_required = this.testService.getTestBinaryState(this.image_required_options, this.test.images_required);
        this.crew_perform = this.testService.getTestBinaryState(this.crew_perform_options, this.test.crew_perform);
        this.test_completed = this.testService.getTestBinaryState(this.test_completed_options, this.test.test_completed);
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.onEditModeChange(res);
      });
  }

  onEditModeChange(isReadonly: boolean) {
    this.isReadonly = isReadonly;
    if (isReadonly) {
      this.fieldAppearance = 'outline';
    } else {
      this.fieldAppearance = 'fill';
    }
  }

  onContentChange() {
    this.isContentChanged = true;
  }

  imageRequiredOptionChanged() {
    this.test.images_required = this.image_required.state;
    this.isContentChanged = true;
  }

  crewPerformOptionChanged() {
    this.test.crew_perform = this.crew_perform.state;
    this.isContentChanged = true;
  }

  testCompletedOptionChanged() {
    this.test.test_completed = this.test_completed.state;
    this.isContentChanged = true;
  }

  // --- CRUD actions ---

  createTest() {
    this.testService.createTest(this.test.trail_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addModelToSytem(model);
        },
        error: err => { console.log(err); }
      });
  }

  updateTest() {
    this.isContentChanged = false; // Reset the state, and disable the button to prevent multiple clicks
    this.testService.updateTest(this.test)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.test = model;
          this.notifyService.updateTestSelection(model);
        },
        error: err => { console.log(err); }
      });
  }

  deleteTest() {
    this.testService.deleteTest(this.test.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeTestAfterDelete();
        },
        error: err => { console.log(err); }
      });
  }

  private addModelToSytem(model: Test) {
    this.test = model;
    this.notifyService.updateTestSelection(this.test);
    let listTest = this.notifyService.getListAllTests();
    listTest.push(model);
    this.notifyService.updateListAllTests(listTest);
  }

  private removeTestAfterDelete() {
    let listTest = this.notifyService.getListAllTests();
    const index = listTest.indexOf(this.test, 0);
    if (index > -1) {
      listTest.splice(index, 1);
    }
    this.notifyService.updateListAllTests(listTest);

    // Select the current 'trail', then navigate to 'trail-hub', i.e. to load and display the list of tests after deletion.
    let trailModel = this.notifyService.getSelectedTrail();
    this.notifyService.updateTrailSelection(trailModel); //update the same trail selection, so that adaption of display features in 'app.component.ts' can be triggered. 
    this.router.navigate(['trail-hub']);
  }

  btnDeleteClicked() {
    if (!this.isReadonly) {
      let message = "The whole test will be deleted, sure to proceed?" 
      const dialogRef = this._dialogConfirm.open(ConfirmDialogComponent, { data: message });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.confirmed) {
              this.deleteTest();
            }
          };
        });
    }
  }

}
